import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthenticatedAxios } from "./authentication";
import { getFeatureFlagsFromCache } from "./use-config";
import type { AccessLevel, UserInfoStatus } from "./use-user-info";

export type GateState =
  | "idle"
  | "checking"
  | "onboarding"
  | "ready"
  | "blocked"
  | "error";

interface CheckWhitelistResponse {
  Whitelisting: boolean;
  accessLevel: AccessLevel | null;
}

export interface OnboardingGateState {
  gateState: GateState;
  retry: () => void;
}

export function useOnboardingGate(params: {
  enabled: boolean;
  email: string | undefined;
  userInfoStatus: UserInfoStatus;
  refreshUserInfo: () => Promise<void>;
}): OnboardingGateState {
  const { enabled, email, userInfoStatus, refreshUserInfo } = params;
  const [gateState, setGateState] = useState<GateState>("idle");
  const [retryToken, setRetryToken] = useState(0);
  const inFlight = useRef(false);

  const retry = useCallback(() => setRetryToken((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setGateState("idle");
      return;
    }

    if (!getFeatureFlagsFromCache().onboardingV2) {
      setGateState("ready");
      return;
    }

    if (userInfoStatus === "loading") {
      setGateState("checking");
      return;
    }

    if (userInfoStatus === "row") {
      setGateState("ready");
      return;
    }

    if (userInfoStatus === "error") {
      setGateState("error");
      return;
    }

    if (userInfoStatus !== "missing") {
      return;
    }

    if (!email) {
      setGateState("error");
      return;
    }

    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setGateState("onboarding");
    const { axios } = getAuthenticatedAxios();
    axios
      .post<CheckWhitelistResponse>("/v0/cdc/check-whitelist", { email })
      .then(async (res) => {
        if (res.data.accessLevel === "BLOCKED" || !res.data.Whitelisting) {
          setGateState("blocked");
          return;
        }
        await refreshUserInfo();
        setGateState("ready");
      })
      .catch((err: unknown) => {
        console.error("Onboarding gate: check-whitelist failed", err);
        setGateState("error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [enabled, email, userInfoStatus, retryToken, refreshUserInfo]);

  return { gateState, retry };
}
